import { Badge } from '@startupquickstart/frontend';
import type { CrudConfig } from '@startupquickstart/frontend';
import { format } from 'date-fns';

/**
 * Scanner type - represents a user who has scanned a hotel access code
 * Data is aggregated by user with their most recent scan info
 */
export interface Scanner {
  user: {
    id: string;
    email: string;
    firstName: string | null;
    lastName: string | null;
    imageUrl: string | null;
  };
  mostRecentScan: {
    id: string;
    scannedAt: string;
    expiresAt: string | null;
    isActive: boolean;
  };
  totalScans: number;
}

/**
 * Helper to get user display name
 */
function getUserDisplayName(scanner: Scanner | undefined): string {
  if (!scanner?.user) return 'N/A';
  const { firstName, lastName, email } = scanner.user;
  if (firstName || lastName) {
    return `${firstName || ''} ${lastName || ''}`.trim();
  }
  return email;
}

/**
 * Helper to format date
 */
function formatDate(dateString: string | null | undefined): string {
  if (!dateString) return '-';
  return format(new Date(dateString), 'MMM d, yyyy');
}

/**
 * Scanner CRUD configuration for the list view
 * Note: This is a read-only config - no create/update/delete operations
 */
export const scannerCrudConfig: CrudConfig<Scanner> = {
  entityName: 'Scanned User',
  entityNamePlural: 'Scanned Users',

  endpoints: {
    // These will be overridden when used - set dynamically based on hotelId or accessCodeId
    list: '/hotels/{hotelId}/scanners',
    single: (id: string) => `/scanners/${id}`,
    create: '/scanners', // Not used
    update: (id: string) => `/scanners/${id}`, // Not used
    delete: (id: string) => `/scanners/${id}`, // Not used
  },

  fields: [
    {
      key: 'user.name' as keyof Scanner,
      label: 'User',
      type: 'custom',
      format: (_value, scanner) => getUserDisplayName(scanner),
    },
    {
      key: 'user.email' as keyof Scanner,
      label: 'Email',
      type: 'custom',
      format: (_value, scanner) => scanner?.user?.email || 'N/A',
    },
    {
      key: 'mostRecentScan.isActive' as keyof Scanner,
      label: 'Status',
      type: 'custom',
      format: (_value, scanner) => {
        const isActive = scanner?.mostRecentScan?.isActive;
        return isActive ? (
          <Badge className="bg-green-500/10 text-green-600 border-green-500/20">
            Active
          </Badge>
        ) : (
          <Badge variant="secondary">Expired</Badge>
        );
      },
    },
    {
      key: 'mostRecentScan.scannedAt' as keyof Scanner,
      label: 'Access Gained',
      type: 'custom',
      format: (_value, scanner) => formatDate(scanner?.mostRecentScan?.scannedAt),
    },
    {
      key: 'mostRecentScan.expiresAt' as keyof Scanner,
      label: 'Expires',
      type: 'custom',
      format: (_value, scanner) => formatDate(scanner?.mostRecentScan?.expiresAt),
    },
    {
      key: 'totalScans',
      label: 'Scans',
      type: 'number',
    },
  ],

  listView: {
    fields: ['user.name', 'user.email', 'mostRecentScan.isActive', 'mostRecentScan.scannedAt', 'mostRecentScan.expiresAt', 'totalScans'] as (keyof Scanner)[],
    searchableFields: [],
    sortableFields: ['scannedAt', 'expiresAt', 'totalScans'] as (keyof Scanner)[],
    defaultSort: {
      field: 'scannedAt' as keyof Scanner,
      direction: 'desc',
    },
    itemsPerPage: 10,
    actions: {
      view: {
        enabled: true,
      },
    },
  },

  detailView: {
    sections: [],
  },

  form: {
    sections: [],
  },
};

/**
 * Create a scanner config for a specific hotel
 */
export function createHotelScannerConfig(hotelId: string): CrudConfig<Scanner> {
  return {
    ...scannerCrudConfig,
    endpoints: {
      ...scannerCrudConfig.endpoints,
      list: `/hotels/${hotelId}/scanners`,
    },
  };
}

/**
 * Create a scanner config for a specific access code
 */
export function createAccessCodeScannerConfig(accessCodeId: string): CrudConfig<Scanner> {
  return {
    ...scannerCrudConfig,
    endpoints: {
      ...scannerCrudConfig.endpoints,
      list: `/hotel-access-codes/${accessCodeId}/scanners`,
    },
  };
}
